/**
 * 管理员数据管理 Hook
 * 统一管理所有管理员相关的数据状态
 */
import { useState, useCallback } from "react";
import { AdminRecord } from "../config/panelTableConfig";
import { AdminService } from "../services/adminService";
import { PaginationParams } from "../services/adminService";
import { PermissionService } from "../services/permissionService";
import { RoleService } from "../services/roleService";

export interface UseAdminDataReturn {
  // 管理员列表相关
  adminList: AdminRecord[];
  adminLoading: boolean;
  adminTotal: number;
  fetchAdminList: (params?: PaginationParams) => Promise<void>;

  // 权限相关
  permissions: any[];
  permissionLoading: boolean;
  fetchPermissions: (adminId: string) => Promise<any[]>;
  clearPermissions: () => void;

  // 权限数量相关
  permissionCounts: Record<string, number>;
  permissionCountLoading: boolean;
  loadPermissionCounts: (adminIds: string[]) => Promise<void>;
  updatePermissionCount: (adminId: string, count: number) => void;

  // 角色相关
  allRoles: any[];
  selectedRoles: string[];
  roleLoading: boolean;
  fetchAdminRoles: (adminId: string) => Promise<void>;
  setSelectedRoles: (roles: string[]) => void;
  clearRoles: () => void;
}

export const useAdminData = (): UseAdminDataReturn => {
  // 管理员列表状态
  const [adminList, setAdminList] = useState<AdminRecord[]>([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminTotal, setAdminTotal] = useState(0);

  // 权限状态
  const [permissions, setPermissions] = useState<any[]>([]);
  const [permissionLoading, setPermissionLoading] = useState(false);

  // 权限数量状态
  const [permissionCounts, setPermissionCounts] = useState<Record<string, number>>({});
  const [permissionCountLoading, setPermissionCountLoading] = useState(false);

  // 角色状态
  const [allRoles, setAllRoles] = useState<any[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [roleLoading, setRoleLoading] = useState(false);

  // 获取管理员列表
  const fetchAdminList = useCallback(async (params: PaginationParams = {}) => {
    setAdminLoading(true);
    try {
      const response = await AdminService.getAdminList(params);
      if (response.success) {
        setAdminList(response.data.items);
        setAdminTotal(response.data.total);
      }
    } catch (error) {
      console.error("获取管理员列表失败:", error);
    } finally {
      setAdminLoading(false);
    }
  }, []);

  // 获取管理员权限
  const fetchPermissions = useCallback(async (adminId: string) => {
    if (!adminId) return [];

    console.log("🔍 开始获取管理员权限:", adminId);
    setPermissionLoading(true);
    try {
      const response = await PermissionService.getAdminPermissions(adminId);
      console.log("📡 权限接口响应:", response);

      if (response.success) {
        const permissions = response.data?.permissions || [];
        console.log("📋 设置权限数据:", permissions);
        setPermissions(permissions);
        return permissions;
      } else {
        console.error("权限接口返回失败:", response);
        setPermissions([]);
        return [];
      }
    } catch (error) {
      console.error("获取管理员权限失败:", error);
      setPermissions([]);
      return [];
    } finally {
      setPermissionLoading(false);
    }
  }, []);

  // 清除权限数据
  const clearPermissions = useCallback(() => {
    setPermissions([]);
    setPermissionLoading(false);
  }, []);

  // 加载权限数量
  const loadPermissionCounts = useCallback(async (adminIds: string[]) => {
    if (adminIds.length === 0) return;

    console.log("开始加载权限数量，管理员IDs:", adminIds);
    setPermissionCountLoading(true);
    try {
      // 使用批量接口（内部会并行调用单个接口）
      const counts = await PermissionService.getBatchAdminPermissionCounts(adminIds);
      console.log("权限数量加载结果:", counts);
      setPermissionCounts(prev => ({ ...prev, ...counts }));
    } catch (error) {
      console.error("加载权限数量失败:", error);
    } finally {
      setPermissionCountLoading(false);
    }
  }, []);

  // 更新单个管理员的权限数量
  const updatePermissionCount = useCallback((adminId: string, count: number) => {
    setPermissionCounts(prev => ({ ...prev, [adminId]: count }));
  }, []);

  // 获取管理员角色
  const fetchAdminRoles = useCallback(async (adminId: string) => {
    if (!adminId) return;

    setRoleLoading(true);
    try {
      const response = await RoleService.getAdminRoles(adminId);
      if (response.success) {
        setAllRoles(response.data || []);

        // 从响应中提取已选中的角色ID
        const selectedRoleIds = response.data
          .filter((role: any) => role.isAssigned)
          .map((role: any) => role.id);
        setSelectedRoles(selectedRoleIds);
      }
    } catch (error) {
      console.error("获取管理员角色失败:", error);
      setAllRoles([]);
      setSelectedRoles([]);
    } finally {
      setRoleLoading(false);
    }
  }, []);

  // 设置选中的角色（使用状态更新函数）
  const updateSelectedRoles = useCallback((roles: string[]) => {
    setSelectedRoles(roles);
  }, []);

  // 清除角色数据
  const clearRoles = useCallback(() => {
    setAllRoles([]);
    setSelectedRoles([]);
    setRoleLoading(false);
  }, []);

  return {
    // 管理员列表相关
    adminList,
    adminLoading,
    adminTotal,
    fetchAdminList,

    // 权限相关
    permissions,
    permissionLoading,
    fetchPermissions,
    clearPermissions,

    // 权限数量相关
    permissionCounts,
    permissionCountLoading,
    loadPermissionCounts,
    updatePermissionCount,

    // 角色相关
    allRoles,
    selectedRoles,
    roleLoading,
    fetchAdminRoles,
    setSelectedRoles: updateSelectedRoles,
    clearRoles,
  };
};
